import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import uniqid from 'uniqid'

import styled from '@emotion/styled'
import { Link } from '../link'

export const TABBED_VARIANT_DEFAULT = 'default'
export const TABBED_VARIANT_LIGHT = 'light'

const handleMargin = ({ margin }) =>
  margin !== undefined ? `margin: ${margin};` : ''

const StyledContainer = styled.div`
  ${handleMargin}
`

const handleContentVariant = props =>
  props.variant === TABBED_VARIANT_LIGHT
    ? `
padding: 0;
border: 0;
`
    : ''

const StyledContent = styled.div`
  background: white;
  padding: 24px 32px;
  border: 1px solid var(--geyser);
  border-radius: 0 4px 4px 4px;
  ${handleContentVariant}
`

const StyledNav = styled.nav`
  padding-left: 0;
  font-size: 14px;
`

const handleTabContainerVariant = props =>
  props.variant === TABBED_VARIANT_LIGHT
    ? `
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
    font-size: 15px;
    font-weight: 400;
    border-bottom: 2px solid var(--geyser);

`
    : ''

const StyledTabContainer = styled.ol`
  ${handleTabContainerVariant}
`

const handleTabItemVariant = props =>
  props.variant === TABBED_VARIANT_LIGHT
    ? `
    margin: 0;
    padding: 0 0 6px 0;
    border: 0;
    border-bottom: 2px solid transparent;
    background: #fff;

    transition: all 0.2s ease;

    :hover {
      background: #fff;
      border-bottom: 2px solid var(--blue-chill);
    }

    a {
      padding: 0;
      font-size: 15px;
      font-weight: 400;
    }

    a:hover {
      background-color: #fff;;
    }

    

`
    : ''

const handleTabItemActive = props =>
  props.variant === TABBED_VARIANT_LIGHT && props.active
    ? `
      border-bottom: 2px solid var(--java);

      a {
        font-weight: 500;
      }
    
    `
    : ''

const StyledTabItem = styled.li`
  display: inline-block;
  list-style: none;
  margin-bottom: -1px;
  margin-right: 8px;
  border: 1px solid var(--geyser);
  border-radius: 4px 4px 0px 0px;
  background-color: var(--wild-sand);

  a:hover {
    background: var(--mercury01);
    border-radius: 3px 3px 0px 0px;
  }
  ${props =>
    props.active &&
    `
    font-weight: 500;
    background-color: white;
    cursor: default;
    border-bottom: 1px solid white;
    a:hover {
      cursor: default;
      background: white;
    }
  `}
  a {
    padding: 8px 16px;
    display: block;
  }

  ${handleTabItemVariant}
  ${handleTabItemActive}
`

const Tab = ({ label, className, disabled, active, onClick, id, variant }) => (
  <StyledTabItem active={active} data-mu-test={'tab-' + id} variant={variant}>
    <Link
      href='#'
      data-mu-test={'tab-link-' + id}
      onClick={e => {
        e.preventDefault()
        onClick(e)
      }}
    >
      {label}
    </Link>
  </StyledTabItem>
)

Tab.defaultProps = {
  onClick: () => { },
  variant: TABBED_VARIANT_DEFAULT
}

Tab.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf([TABBED_VARIANT_DEFAULT, TABBED_VARIANT_LIGHT])
}

export const Tabbed = ({
  onChange,
  tabs,
  defaultActive,
  'data-mu-test': dataAttributeTest,
  variant,
  margin,
  className
}) => {
  const tabsWithId = tabs.filter(tab => tab.id)
  const { current: uuid } = useRef(uniqid('tabbed'))
  const [active, setActive] = useState(defaultActive || tabsWithId[0].id)

  useEffect(() => setActive(defaultActive || tabsWithId[0].id), [defaultActive])

  const handleClickTab = id => {
    const selectedTab = tabsWithId.find(tab => tab.id === id)
    setActive(id)
    typeof selectedTab.onClick === 'function'
      ? selectedTab.onClick() && onChange(id)
      : onChange(id)
  }

  const activeTab = tabs.find(tab => tab.id === active)

  return (
    <StyledContainer
      data-mu-test={dataAttributeTest}
      variant={variant}
      margin={margin}
      className={className}
    >
      <StyledNav variant={variant}>
        <StyledTabContainer variant={variant}>
          {tabsWithId.map((tab, i) => (
            <Tab
              variant={variant}
              className={tab.className}
              key={`${uuid}-tab-${tab.id}-${i}`}
              label={tab.label}
              active={tab.id === active}
              disabled={tab.disabled}
              id={tab.id}
              onClick={() => handleClickTab(tab.id)}
            />
          ))}
        </StyledTabContainer>
      </StyledNav>
      <StyledContent variant={variant}>
        {activeTab && (
          <section key={`tab-{${uuid}}-${activeTab.id}`}>
            {activeTab.content}
          </section>
        )}
      </StyledContent>
    </StyledContainer>
  )
}

Tabbed.defaultProps = {
  onChange: () => null,
  variant: TABBED_VARIANT_DEFAULT,
  margin: '0 0 24px 0'
}

Tabbed.propTypes = {
  /**
   * Callback triggered on content change
   * @param {number} index index of the activated tab
   * @returns {boolean} if false prevents triggering parent's onChange callback
   */
  onChange: PropTypes.func,
  /** Array of props object for each navigation tab */
  className: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      /* additional classNames useful for peculiar styling */
      className: PropTypes.string,
      /* whether or not the tab is disabled */
      disabled: PropTypes.bool,
      /* unique identifier of the content the tab refers to */
      id: PropTypes.string,
      /* label of the Tab itself */
      label: PropTypes.string,
      /**
       * Additional tab specific callback
       * @returns {boolean} if false prevents triggering parent's onChange callback
       */
      onClick: PropTypes.func,
      /* content of the Tab itself */
      content: PropTypes.element
    })
  ),
  'data-mu-test': PropTypes.string,
  /** Default active tab, useful for initial state or external control. */
  defaultActive: (props, propName, componentName) => {
    const isValid = props.tabs
      .map(({ value }) => value)
      .includes(props[propName])
    if (!isValid) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to
        \`${componentName}\`. Prop value must correspond to
        an \`id\` present in of \`props.tabs\` values.`
      )
    }
  },
  variant: PropTypes.oneOf([TABBED_VARIANT_DEFAULT, TABBED_VARIANT_LIGHT]),
  margin: PropTypes.string
}
